<script setup lang="ts">
import type { Option } from './index.vue'

const props = withDefaults(
    defineProps<{
        query?: string
        autocomplete?: boolean
        selected?: any
        multiple?: boolean
        noSelectionText?: string
        selectionText?: string
        options?: Option[]
        small?: boolean
        large?: boolean
    }>(),
    {
        noSelectionText: 'No items selected',
        selectionText: '{value} items selected',
        selected: undefined,
        options: undefined,
        query: undefined
    }
)

const emit = defineEmits<{
    (_e: 'clear'): void
    (_e: 'update:query', _value?: string): void
}>()

const hasSelection = computed(() => {
    if (props.multiple) {
        return props.selected.length > 0
    }

    return Boolean(props.selected)
})

const displayValue = computed(() => {
    if (!unref(hasSelection)) {
        return props.noSelectionText
    }

    if (Array.isArray(props.selected)) {
        if (props.selected.length > 2)
            return props.selectionText.replace(
                /\{value\}/g,
                props.selected.length.toString()
            )

        return props.selected.map(option => option.label).join(', ')
    }

    return props.selected.label
})
</script>

<template>
    <UiButton :small="small" :large="large">
        <template #button>
            <ComboboxButton
                v-if="autocomplete"
                autocomplete="off"
                class="flex w-full items-center gap-2"
            >
                <MagnifyingGlassIcon class="h-4 flex-shrink-0" />

                <ComboboxInput
                    class="bg-neutral block flex-1 text-[100%] leading-[inherit] placeholder-black outline-none"
                    :placeholder="displayValue"
                    autocomplete="off"
                    :data-query="query"
                    :display-value="() => ''"
                    @change="emit('update:query', $event.target.value)"
                />

                <XMarkIcon
                    v-if="hasSelection"
                    class="h-4 flex-shrink-0 cursor-pointer"
                    @click="emit('clear')"
                />
            </ComboboxButton>

            <ListboxButton
                v-else
                class="bg-neutral flex w-full items-center gap-4 outline-none"
            >
                <span class="block flex-1 truncate text-left">
                    {{ displayValue }}
                </span>

                <span
                    v-if="options"
                    class="pointer-events-none flex items-center"
                >
                    <ChevronDownIcon
                        class="h-4 text-gray-400"
                        aria-hidden="true"
                    />
                </span>
            </ListboxButton>
        </template>
    </UiButton>
</template>